/**
 * Merge catalog + design-library into a fast lookup index.
 */
import { GALLERY_BASE, OCCASIONS, PRIMARY_CHIPS } from "./galleryConstants";
import { buildNcaProductId, buildNcaProductUrl } from "./galleryNcaLink";

export function splitCategories(entry) {
  const raw = [entry?.primary_category, entry?.all_categories].filter(Boolean).join("|");
  const seen = new Set();
  return raw
    .split("|")
    .map((c) => c.trim().toUpperCase())
    .filter((c) => {
      if (!c || seen.has(c)) return false;
      seen.add(c);
      return true;
    });
}

export function mergeRow(photoId, catalogPhoto, libEntry) {
  const meta = catalogPhoto?.metadata || {};
  const base = {
    id: String(photoId),
    primary_category: libEntry?.primary_category || meta.primary_category || "",
    all_categories: libEntry?.all_categories || meta.all_categories || meta.primary_category || "",
    who_for: libEntry?.who_for || meta.who_for || "",
    theme: libEntry?.theme || meta.theme || "",
    character_name: libEntry?.character_name || meta.character_name || "",
    customer_callouts: libEntry?.customer_callouts || meta.customer_callouts || "",
    display_name: libEntry?.display_name || meta.display_name || catalogPhoto?.display_name || "",
    decor_level: libEntry?.decor_level || meta.decor_level || "Medium",
    photo_url: libEntry?.photo_url || catalogPhoto?.url || "",
    nobg_url: libEntry?.nobg_url || "",
    folder: catalogPhoto?.category || libEntry?.source_category || "",
    category: catalogPhoto?.category || "",
    subpath: catalogPhoto?.subpath || "",
    file: catalogPhoto?.file || "",
    is_curated_liked:
      libEntry?.is_curated_liked === true || libEntry?.is_curated_liked === "true",
  };
  if (libEntry && typeof libEntry === "object") {
    for (const [key, value] of Object.entries(libEntry)) {
      if (value == null || value === "") continue;
      if (!(key in base) || base[key] === "" || base[key] == null) {
        base[key] = value;
      }
    }
  }
  if (meta && typeof meta === "object") {
    for (const [key, value] of Object.entries(meta)) {
      if (value == null || value === "") continue;
      if (!(key in base) || base[key] === "" || base[key] == null) {
        base[key] = value;
      }
    }
  }
  return base;
}

/** Split optional ?v=… (or any query) so path munging does not break cache-bust tokens. */
function splitUrlQuery(raw) {
  const s = String(raw || "");
  const q = s.indexOf("?");
  if (q < 0) return { path: s, query: "" };
  return { path: s.slice(0, q), query: s.slice(q) };
}

export function resolveImageUrl(row) {
  const raw = row.nobg_url || row.photo_url || "";
  if (!raw) return "";
  if (raw.startsWith("http")) return raw;
  const { path: bare, query } = splitUrlQuery(raw.replace(/^\//, ""));
  if (bare.startsWith("images/")) return `${GALLERY_BASE}/${bare}${query}`;
  return `${GALLERY_BASE}/${bare}${query}`;
}

/** Grid thumbnail URL — maps images/… → images-thumb/… (falls back to full if absent). */
export function resolveThumbUrl(row) {
  const raw = row.nobg_url || row.photo_url || "";
  if (!raw) return "";
  if (raw.startsWith("http")) return raw;
  const { path: bareIn, query } = splitUrlQuery(raw.replace(/^\//, ""));
  let path = bareIn;
  if (path.startsWith("images/")) {
    path = `images-thumb/${path.slice("images/".length)}`;
  } else if (!path.startsWith("images-thumb/")) {
    path = `images-thumb/${path}`;
  }
  // Always serve .webp thumbs (strip extension before any query was already split).
  path = path.replace(/\.(jpe?g|png|webp)$/i, ".webp");
  return `${GALLERY_BASE}/${path}${query}`;
}

function whoForTokens(row) {
  return String(row?.who_for || "")
    .split("|")
    .map((w) => w.trim().toLowerCase())
    .filter(Boolean);
}

/** Map WHO FOR tags → occasion chip ids (FOR HER, KIDS, …). */
const WHO_FOR_TO_OCCASION = {
  "for her": "FOR HER",
  her: "FOR HER",
  "for him": "FOR HIM",
  him: "FOR HIM",
  kids: "KIDS",
  babies: "BABIES",
  anniversary: "ANNIVERSARY",
  wedding: "WEDDING",
  corporate: "CORPORATE",
  "baby shower": "BABY SHOWER",
  "bride to be": "BRIDE TO BE",
  bento: "BENTO",
  cupcakes: "CUPCAKES",
  "cheese cakes": "CHEESE CAKES",
  "cheese cake": "CHEESE CAKES",
  seniors: "SENIORS",
  festival: "FESTIVAL",
  congratulations: "CONGRATULATIONS",
};

export function occasionsFromWhoFor(row) {
  const out = new Set();
  for (const token of whoForTokens(row)) {
    const occ = WHO_FOR_TO_OCCASION[token];
    if (occ) out.add(occ);
  }
  return out;
}

function whoForHasGender(row, gender) {
  const who = whoForTokens(row);
  if (gender === "girl") return who.some((w) => w === "girls" || w === "girl");
  if (gender === "boy") return who.some((w) => w === "boys" || w === "boy");
  return false;
}

function relationshipTags(row) {
  return String(row.relationship_tags || "")
    .split("|")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * TT-847 — mirrors `RELATIONSHIP_TAG_LANE_MAP` in
 * packages/shared/gallery_catalog_import.py one-for-one (see
 * `test_tt847RelationshipCategoryBridge.py`/`.test.js` for the contract that
 * pins the two tables equal). A blank-category design tagged with one of
 * these keys is no longer unlaned on the backend, so it must not read as
 * "no category" here either — see `selectNoCategoryDesignIds` below.
 *
 * "boss" is deliberately absent: it maps to a lane retired from
 * `MENU_LANES` in apps/mobile/src/data/menuLanes.js (`RETIRED_MENU_LANE_IDS`),
 * so a blank-category "Boss" tag stays unlaned, same as on the backend.
 */
export const RELATIONSHIP_TAG_LANE_MAP = Object.freeze({
  father: "DAD",
  dad: "DAD",
  husband: "HUSBAND",
  boyfriend: "BOYFRIEND",
  brother: "BROTHER",
  mother: "MOTHER",
  mom: "MOTHER",
  maa: "MOTHER",
  wife: "WIFE",
  wifey: "WIFE",
  biwi: "WIFE",
  sister: "SISTER",
  girlfriend: "GIRLFRIEND",
  girl: "GIRL",
  boy: "BOY",
});

/** Does this design carry at least one exact, currently-mapped relationship tag? */
export function hasBridgedRelationshipLane(row) {
  return relationshipTags(row).some((tag) => RELATIONSHIP_TAG_LANE_MAP[tag] != null);
}

function matchesCategoryFilter(design, categoryId) {
  const catId = String(categoryId || "").trim();
  const source = String(design?.row?.source || "").trim().toLowerCase();
  const isCustomCake =
    design?.row?.is_custom_cake === true ||
    String(design?.row?.is_custom_cake || "").trim().toLowerCase() === "true";
  const isMApp = source === "m_app_sync" && isCustomCake;
  if (catId === "__new__") return source === "manual";
  if (catId === "__m_app__") return isMApp;
  if (!catId || catId === "__all__") return true;
  if (source === "manual" || source === "m_app_sync") return false;

  if (catId === "__liked__") return design.isLiked;

  // Prefer cached sets (built once in toDesignItem)
  const catSet = design.catSet;
  const occSet = design.occSet;
  const whoForSet = design.whoForSet;
  const relSet = design.relSet;

  if (catId.startsWith("occ:")) {
    const occ = catId.slice(4).toUpperCase();
    if (catSet) {
      if (catSet.has(occ)) return true;
      return Boolean(occSet?.has(occ));
    }
    if (design.categories.includes(occ)) return true;
    return occasionsFromWhoFor(design.row).has(occ);
  }

  if (catId === "rel:GIRLS") {
    if (design.isKidish != null) {
      return design.isKidish && design.hasGirl;
    }
    const kidish =
      design.categories.some((c) => c === "KIDS" || c === "BABIES") ||
      occasionsFromWhoFor(design.row).has("KIDS") ||
      occasionsFromWhoFor(design.row).has("BABIES") ||
      whoForTokens(design.row).some((w) => w === "kids" || w === "babies");
    return kidish && whoForHasGender(design.row, "girl");
  }

  if (catId === "rel:BOYS") {
    if (design.isKidish != null) {
      return design.isKidish && design.hasBoy;
    }
    const kidish =
      design.categories.some((c) => c === "KIDS" || c === "BABIES") ||
      occasionsFromWhoFor(design.row).has("KIDS") ||
      occasionsFromWhoFor(design.row).has("BABIES") ||
      whoForTokens(design.row).some((w) => w === "kids" || w === "babies");
    return kidish && whoForHasGender(design.row, "boy");
  }

  if (catId.startsWith("rel:")) {
    const parts = catId.slice(4).split(":");
    const occ = (parts[0] || "").toUpperCase();
    const rel = parts.slice(1).join(":").toLowerCase();
    if (catSet) {
      if (!catSet.has(occ)) return false;
      if (!rel) return true;
      return Boolean(relSet?.has(rel));
    }
    if (!design.categories.includes(occ)) return false;
    if (!rel) return true;
    return relationshipTags(design.row).includes(rel);
  }

  if (catId.startsWith("folder:")) {
    const folder = catId.slice(7);
    return design.folder === folder;
  }

  const upper = catId.toUpperCase();
  if (catSet) return catSet.has(upper);
  return design.categories.includes(upper);
}

export function toDesignItem(photoId, row, likedSet) {
  const url = resolveImageUrl(row);
  if (!url) return null;
  const isLiked = likedSet.has(String(photoId)) || row.is_curated_liked;
  const ncaProductId = buildNcaProductId(photoId, row);
  const shareUrl = buildNcaProductUrl(photoId, row);
  const categories = splitCategories(row);
  const title =
    row.display_name || row.character_name || row.theme || `Design ${photoId}`;

  const whoTokens = whoForTokens(row);
  const catSet = new Set(categories);
  const occSet = new Set();
  for (const token of whoTokens) {
    const occ = WHO_FOR_TO_OCCASION[token];
    if (occ) occSet.add(occ);
  }
  const whoForSet = new Set(whoTokens);
  const relSet = new Set(relationshipTags(row));
  const isKidish =
    catSet.has("KIDS") ||
    catSet.has("BABIES") ||
    occSet.has("KIDS") ||
    occSet.has("BABIES") ||
    whoForSet.has("kids") ||
    whoForSet.has("babies");
  const hasGirl = whoForSet.has("girls") || whoForSet.has("girl");
  const hasBoy = whoForSet.has("boys") || whoForSet.has("boy");

  return {
    id: String(photoId),
    url,
    thumb: resolveThumbUrl(row) || url,
    folder: row.folder || "",
    categories,
    primaryCategory: String(row.primary_category || "").trim().toUpperCase(),
    theme: row.theme || "",
    character: row.character_name || "",
    tags: row.customer_callouts || "",
    title,
    decorLevel: row.decor_level || "Medium",
    whoFor: row.who_for || "",
    isLiked,
    workflowStatus: String(row.workflow_status || "").trim().toUpperCase(),
    ncaProductId,
    shareUrl,
    row,
    catSet,
    occSet,
    whoForSet,
    relSet,
    isKidish,
    hasGirl,
    hasBoy,
  };
}

/**
 * The liked predicate the heart actually renders.
 *
 * `toDesignItem` treats a curated-liked row as liked even when it is absent
 * from liked-design-ids.json, so anything deciding what a tap should *do* must
 * read this and never `likedSet` alone: on a curated design the raw set says
 * "not liked", so the first tap would re-like an already-filled heart and
 * nothing would visibly change.
 */
export function isDesignLiked(index, photoId) {
  const id = String(photoId);
  const design = index?.designById?.get(id);
  if (design) return Boolean(design.isLiked);
  return Boolean(index?.likedSet?.has(id));
}

/**
 * Single-pass chip counts — O(designs), not O(designs × chips).
 */
export function rebuildChips(allDesigns, catalog) {
  const primaryCounts = new Map();
  for (const chip of PRIMARY_CHIPS) primaryCounts.set(chip.id, 0);

  const occasionCounts = new Map();
  for (const o of OCCASIONS) occasionCounts.set(o, 0);

  const folderCounts = new Map();
  for (const c of catalog?.categories || []) folderCounts.set(c.name, 0);

  for (const d of allDesigns) {
    // Primary chips that aren't liked
    for (const chip of PRIMARY_CHIPS) {
      if (chip.id === "__liked__") continue;
      if (matchesCategoryFilter(d, chip.id)) {
        primaryCounts.set(chip.id, (primaryCounts.get(chip.id) || 0) + 1);
      }
    }

    const source = String(d?.row?.source || "").trim().toLowerCase();
    if (source === "manual" || source === "m_app_sync") {
      continue;
    }

    if (d.isLiked) {
      primaryCounts.set("__liked__", (primaryCounts.get("__liked__") || 0) + 1);
    }

    // Occasions: category set ∪ who-for occ set
    const seenOcc = new Set();
    if (d.catSet) {
      for (const c of d.catSet) {
        if (occasionCounts.has(c) && !seenOcc.has(c)) {
          seenOcc.add(c);
          occasionCounts.set(c, (occasionCounts.get(c) || 0) + 1);
        }
      }
    }
    if (d.occSet) {
      for (const c of d.occSet) {
        if (occasionCounts.has(c) && !seenOcc.has(c)) {
          seenOcc.add(c);
          occasionCounts.set(c, (occasionCounts.get(c) || 0) + 1);
        }
      }
    } else if (!d.catSet) {
      // Fallback without caches
      for (const o of OCCASIONS) {
        if (matchesCategoryFilter(d, `occ:${o}`)) {
          occasionCounts.set(o, (occasionCounts.get(o) || 0) + 1);
        }
      }
    }

    if (d.folder && folderCounts.has(d.folder)) {
      folderCounts.set(d.folder, (folderCounts.get(d.folder) || 0) + 1);
    }
  }

  const occasionChips = OCCASIONS.map((o) => ({
    id: `occ:${o}`,
    label: o.replace("FOR ", ""),
    count: occasionCounts.get(o) || 0,
    kind: "occasion",
  })).filter((c) => c.count > 0);

  const folderChips = (catalog?.categories || []).map((c) => ({
    id: `folder:${c.name}`,
    label: c.name,
    count: folderCounts.get(c.name) || 0,
    kind: "folder",
  }));

  return [
    ...PRIMARY_CHIPS.map((c) => ({
      ...c,
      count: primaryCounts.get(c.id) || 0,
      kind: c.id === "__liked__" ? "liked" : "primary",
    })),
    ...occasionChips.filter((o) => !PRIMARY_CHIPS.some((p) => p.id === o.id)),
    ...folderChips,
  ];
}

export function buildGalleryIndex(catalog, library, likedIdsJson) {
  const libPhotos = library?.photos || {};
  const likedSet = new Set((likedIdsJson?.ids || []).map((id) => String(id)));
  const catalogById = new Map();

  for (const p of catalog?.photos || []) {
    catalogById.set(String(p.id), p);
  }

  const designById = new Map();
  const seen = new Set();

  for (const p of catalog?.photos || []) {
    const id = String(p.id);
    const libEntry = libPhotos[id] || null;
    const row = mergeRow(id, p, libEntry);
    const design = toDesignItem(id, row, likedSet);
    if (!design) continue;
    designById.set(id, design);
    seen.add(id);
  }

  for (const [id, libEntry] of Object.entries(libPhotos)) {
    if (seen.has(id)) continue;
    const catalogPhoto = catalogById.get(id) || null;
    const row = mergeRow(id, catalogPhoto, libEntry);
    const design = toDesignItem(id, row, likedSet);
    if (!design) continue;
    designById.set(id, design);
  }

  const allDesigns = [...designById.values()].sort(
    (a, b) => Number(b.id) - Number(a.id)
  );

  const chips = rebuildChips(allDesigns, catalog);

  return {
    designById,
    allDesigns,
    chips,
    likedSet,
    catalog,
  };
}

/** First N designs (TT-846), fixed by canonical unfiltered order, with no
 * customer category resolved. Reuses `design.categories` (already resolved
 * from the catalog/library rows) rather than duplicating category mapping.
 *
 * TT-847: a blank-category design that carries a bridged relationship tag
 * (`hasBridgedRelationshipLane`) is laned on the backend now, not unlaned —
 * it must not keep showing the "no category" dot, or the Designs app would
 * claim uncategorized for a design that's already live on the Customer app. */
export const NO_CATEGORY_DOT_LIMIT = 20;

export function selectNoCategoryDesignIds(allDesigns, limit = NO_CATEGORY_DOT_LIMIT) {
  const ids = [];
  for (const design of allDesigns) {
    if (design.categories.length === 0 && !hasBridgedRelationshipLane(design.row)) {
      ids.push(design.id);
      if (ids.length >= limit) break;
    }
  }
  return new Set(ids);
}

/**
 * Filter designs for the gallery grid.
 * - Empty query → category browsing only.
 * - Pure numeric query → ID lookup (exact first via Map; else id-includes). Overrides category.
 * - Word query → search only inside the current category (cheap, no full-catalog haystack).
 */
export function filterDesigns(allDesigns, categoryId, searchQuery = "", designById = null) {
  const raw = String(searchQuery || "").trim();
  if (!raw) {
    return allDesigns.filter((d) => matchesCategoryFilter(d, categoryId));
  }

  // Design ID path — jump straight to that design (no full text scan).
  if (/^\d+$/.test(raw)) {
    if (designById) {
      const hit = designById.get(raw);
      if (hit) return [hit];
    }
    return allDesigns.filter((d) => String(d.id).includes(raw));
  }

  // Words — only the open category drawer.
  const q = raw.toLowerCase();
  return allDesigns.filter((d) => {
    if (!matchesCategoryFilter(d, categoryId)) return false;
    const hay = [
      String(d.id),
      d.title,
      d.theme,
      d.character,
      d.tags,
      d.decorLevel,
      d.folder,
      d.categories.join(" "),
      d.whoFor,
    ]
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });
}

/** Update one design in place — no re-sort, no synchronous chip recompute. */
export function patchDesignInIndex(index, photoId, patch) {
  const id = String(photoId);
  const design = index.designById.get(id);
  if (!design) return index;
  const row = { ...design.row, ...patch };
  const designNew = toDesignItem(photoId, row, index.likedSet);
  if (!designNew) return index;
  const designById = new Map(index.designById);
  designById.set(id, designNew);
  const allDesigns = index.allDesigns.slice();
  const idx = allDesigns.findIndex((d) => d.id === id);
  if (idx >= 0) allDesigns[idx] = designNew;
  return {
    ...index,
    designById,
    allDesigns,
    chips: index.chips,
  };
}

/** Like toggle without full rebuild+sort. */
export function patchLikedInIndex(index, photoId, liked) {
  const id = String(photoId);
  const design = index.designById.get(id);
  if (!design) return index;
  const likedSet = new Set(index.likedSet);
  if (liked) likedSet.add(id);
  else likedSet.delete(id);
  const designNew = {
    ...design,
    isLiked: liked,
    row: { ...design.row, is_curated_liked: liked },
  };
  const designById = new Map(index.designById);
  designById.set(id, designNew);
  const allDesigns = index.allDesigns.slice();
  const idx = allDesigns.findIndex((d) => d.id === id);
  if (idx >= 0) allDesigns[idx] = designNew;
  return {
    ...index,
    likedSet,
    designById,
    allDesigns,
    chips: index.chips,
  };
}
